import * as Sentry from '@sentry/react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { RouterProvider, createRouter } from '@tanstack/react-router'
import { Provider as JotaiProvider } from 'jotai'
import React from 'react'
import ReactDOM from 'react-dom/client'

import '@/styles/globals.css'

import { ThemeProvider } from '@/components/theme/theme-provider.tsx'

import { routeTree } from './routeTree.gen'

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            // If we request two queries very close to each other, only the
            // first one will be fetched
            staleTime: 1000,
        },
    },
})

export const router = createRouter({
    routeTree,
    context: {
        queryClient,
    },
    defaultPreloadStaleTime: 0,
})

if (import.meta.env.VITE_SENTRY_DSN) {
    Sentry.init({
        environment: process.env.NODE_ENV,
        dsn: import.meta.env.VITE_SENTRY_DSN,
        integrations: [
            Sentry.browserTracingIntegration(),
            Sentry.browserProfilingIntegration(),
            Sentry.tanstackRouterBrowserTracingIntegration(router),
            Sentry.replayIntegration(),
        ],
        tracesSampleRate: 1.0,
        tracePropagationTargets: [],
        profilesSampleRate: 1.0,
    })
}

declare module '@tanstack/react-router' {
    interface Register {
        router: typeof router
    }
}

ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
        <JotaiProvider>
            <QueryClientProvider client={queryClient}>
                <ThemeProvider defaultTheme="system" storageKey="vite-ui-theme">
                    <RouterProvider router={router} />
                </ThemeProvider>
            </QueryClientProvider>
        </JotaiProvider>
    </React.StrictMode>,
)
